// src/components/Social/SocialMessagesPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import './SocialMessages.css';

// 👇 AJUSTA estos imports si tu árbol difiere
import GeoLocationComponent from '../GeoLocationComponent';
import API_BASE_URL from '../../config/api';

const API_URL = API_BASE_URL;
const HOST = (process.env.REACT_APP_HOST || API_URL).replace(/\/+$/, '');

// 🔧 Normaliza la URL de la foto
const resolvePhotoUrl = (raw, role) => {
  if (!raw) return '';

  const v = String(raw).trim();
  if (!v) return '';

  // URL absoluta
  if (/^https?:\/\//i.test(v)) return v;

  // ya viene como /uploads/...
  if (v.startsWith('/')) return `${HOST}${v}`;

  // sólo nombre de archivo → carpeta según rol
  if (role === 'trainer') {
    return `${HOST}/uploads/trainers/${v}`;
  }
  return `${HOST}/uploads/athletes/${v}`;
};

// 🔧 Helpers
const safeId = (v) => {
  if (v == null) return '';
  const s = String(v).trim();
  if (!s || s === 'null' || s === 'undefined') return '';
  return s;
};

const buildHeaders = (userId, { json = false } = {}) => {
  const uid = safeId(userId);
  const token = safeId(localStorage.getItem('token'));
  const h = {};
  // Content-Type SOLO cuando mandamos body JSON
  if (json) h['Content-Type'] = 'application/json';
  if (uid) h['x-user-id'] = uid;
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

// ✅ Descarga GPX para rutas compartidas
async function downloadGpx(fileId, currentUserId, matchId) {
  const cleanId = String(fileId || '').trim();
  if (!cleanId) throw new Error('fileId inválido.');

  const res = await fetch(
    `${API_URL}/api/fit/gpx/${encodeURIComponent(cleanId)}?matchId=${encodeURIComponent(matchId || '')}`,
    {
      // ✅ autoriza con el usuario logueado (x-user-id)
      headers: buildHeaders(currentUserId),
    }
  );

  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(txt || `No se pudo descargar GPX (HTTP ${res.status})`);
  }

  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `tzone-${cleanId}.gpx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

// ✅ matchId defensivo
const isObjectId = (v) => typeof v === 'string' && /^[0-9a-fA-F]{24}$/.test(v);

const pickMatchId = (m) => {
  const id = m?.matchId || m?._id || m?.id || '';
  return String(id || '').trim();
};

/**
 * Detecta mensajes de ruta:
 *   "... [tzone-fit-route:FILEID]"
 *
 * ✅ FIX: permitir "." (ej: 1765676279504.fit)
 * ✅ FIX: permitir IDs tipo mongo (hex) y guiones/underscores
 */
const ROUTE_TOKEN_RE = /\[tzone-fit-route:([a-zA-Z0-9_.-]+)\]/i;
const ROUTE_TOKEN_RE_FALLBACK = /tzone-fit-route:([a-zA-Z0-9_.-]+)/i;

const parseRouteMessage = (text) => {
  const raw = String(text || '');

  let m = raw.match(ROUTE_TOKEN_RE);
  let usedRe = 'brackets';

  if (!m) {
    m = raw.match(ROUTE_TOKEN_RE_FALLBACK);
    usedRe = 'fallback';
  }

  if (!m) return { isRoute: false };

  const fileId = (m[1] || '').trim();
  if (!fileId) return { isRoute: false };

  const caption =
    usedRe === 'brackets'
      ? raw.replace(ROUTE_TOKEN_RE, '').trim()
      : raw.replace(ROUTE_TOKEN_RE_FALLBACK, '').trim();

  return {
    isRoute: true,
    fileId,
    caption: caption || 'Ruta compartida',
    raw,
  };
};

// Para mostrar preview en la lista izquierda si el último mensaje es ruta
const prettyLastMessage = (text, t) => {
  if (!text) return '';
  const s = String(text);
  return ROUTE_TOKEN_RE.test(s) || ROUTE_TOKEN_RE_FALLBACK.test(s)
    ? t('social.messagesPage.sharedRoute')
    : s;
};

// Fallback: iniciales si no hay foto (o falla la carga)
const initialsFromName = (name) => {
  const s = String(name || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/).filter(Boolean);
  const a = (parts[0] || '').slice(0, 1);
  const b = (parts[1] || '').slice(0, 1);
  return (a + b).toUpperCase() || a.toUpperCase() || '?';
};

// ==========================
//   Helpers resumen
// ==========================
const pad2 = (n) => String(n).padStart(2, '0');

const secondsToHMS = (seconds) => {
  const s = Number(seconds || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  return `${pad2(h)}:${pad2(m)}:${pad2(ss)}`;
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const msToKmh = (ms) => {
  const n = toNumber(ms);
  if (n === null) return null;
  return n * 3.6;
};

const paceMinKm = (distanceKm, seconds) => {
  const d = toNumber(distanceKm);
  const s = toNumber(seconds);
  if (!d || !s) return null;
  const secPerKm = s / d;
  const mm = Math.floor(secPerKm / 60);
  const ss = Math.floor(secPerKm % 60);
  return `${mm}:${pad2(ss)} min/km`;
};

// ==========================
//   Tarjeta resumen FIT
//   (lee /api/fit/parsed-data/:fileId)
// ==========================
const FitSummaryCard = ({ fileId, userId, t }) => {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!fileId) return;

    let cancel = false;

    const run = async () => {
      try {
        setLoading(true);
        setErr('');
        setPayload(null);

        const res = await fetch(`${API_URL}/api/fit/parsed-data/${fileId}`, {
          headers: buildHeaders(userId),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (cancel) return;

        // session puede venir en data.session o data.sessions[0]
        const session = (Array.isArray(data?.sessions) && data.sessions[0]) || data?.session || {};
        const activity = data?.activity || {};
        const sportRaw = (session?.sport || activity?.sport || '').toString().toLowerCase();

        const subSport = session?.sub_sport ? ` - ${session.sub_sport}` : '';
        const sportLabel = sportRaw ? `${sportRaw}${subSport}` : subSport ? subSport.slice(3) : t('social.messagesPage.activity');

        const totalTimer = toNumber(session?.total_timer_time) ?? toNumber(activity?.total_timer_time) ?? 0;
        const distM = toNumber(session?.total_distance) ?? 0;
        const distKm = distM ? distM / 1000 : 0;

        const ascent = toNumber(session?.total_ascent);
        const descent = toNumber(session?.total_descent);
        const calories = toNumber(session?.total_calories);
        const avgHr = toNumber(session?.avg_heart_rate);
        const maxHr = toNumber(session?.max_heart_rate);

        const avgSpeedKmh = msToKmh(session?.enhanced_avg_speed ?? session?.avg_speed);
        const maxSpeedKmh = msToKmh(session?.enhanced_max_speed ?? session?.max_speed);

        const avgCad = toNumber(session?.avg_cadence);
        const maxCad = toNumber(session?.max_cadence);
        const avgPwr = toNumber(session?.avg_power);
        const maxPwr = toNumber(session?.max_power);

        const ts = activity?.timestamp || activity?.local_timestamp || activity?.start_time || null;
        const dateLabel = ts ? new Date(ts).toLocaleString() : '-';

        const isRunning = sportRaw === 'running';
        const computedPace = isRunning ? paceMinKm(distKm, totalTimer) : null;

        setPayload({
          dateLabel,
          sportLabel,
          totalTimer,
          distKm,
          calories,
          ascent,
          descent,
          avgHr,
          maxHr,
          avgSpeedKmh,
          maxSpeedKmh,
          avgCad,
          maxCad,
          avgPwr,
          maxPwr,
          computedPace,
        });
      } catch (e) {
        if (cancel) return;
        setErr(t('social.messagesPage.errors.loadActivitySummary'));
      } finally {
        if (!cancel) setLoading(false);
      }
    };

    run();
    return () => {
      cancel = true;
    };
  }, [fileId, userId, t]);

  if (!fileId) return null;
  if (loading) return <div className="social-fit-summary-loading">{t('social.messagesPage.loadingSummary')}</div>;
  if (err) return <div className="social-fit-summary-error">{err}</div>;
  if (!payload) return null;

  const items = [
    { k: t('social.messagesPage.summary.date'), v: payload.dateLabel },
    { k: t('social.messagesPage.summary.sport'), v: payload.sportLabel },
    { k: t('social.messagesPage.summary.duration'), v: secondsToHMS(payload.totalTimer) },
    { k: t('social.messagesPage.summary.distance'), v: payload.distKm ? `${payload.distKm.toFixed(2)} km` : '-' },
  ];

  if (payload.computedPace) items.push({ k: t('social.messagesPage.summary.avgPace'), v: payload.computedPace });
  if (payload.avgSpeedKmh !== null) items.push({ k: t('social.messagesPage.summary.avgSpeed'), v: `${payload.avgSpeedKmh.toFixed(2)} km/h` });
  if (payload.maxSpeedKmh !== null) items.push({ k: t('social.messagesPage.summary.maxSpeed'), v: `${payload.maxSpeedKmh.toFixed(2)} km/h` });
  if (payload.calories !== null) items.push({ k: t('social.messagesPage.summary.calories'), v: `${Math.round(payload.calories)} kcal` });
  if (payload.ascent !== null) items.push({ k: t('social.messagesPage.summary.ascent'), v: `${Math.round(payload.ascent)} m` });
  if (payload.descent !== null) items.push({ k: t('social.messagesPage.summary.descent'), v: `${Math.round(payload.descent)} m` });
  if (payload.avgHr !== null) items.push({ k: t('social.messagesPage.summary.avgHeartRate'), v: `${Math.round(payload.avgHr)} bpm` });
  if (payload.maxHr !== null) items.push({ k: t('social.messagesPage.summary.maxHeartRate'), v: `${Math.round(payload.maxHr)} bpm` });
  if (payload.avgCad !== null) items.push({ k: t('social.messagesPage.summary.avgCadence'), v: `${Math.round(payload.avgCad)} rpm` });
  if (payload.maxCad !== null) items.push({ k: t('social.messagesPage.summary.maxCadence'), v: `${Math.round(payload.maxCad)} rpm` });
  if (payload.avgPwr !== null) items.push({ k: t('social.messagesPage.summary.avgPower'), v: `${Math.round(payload.avgPwr)} W` });
  if (payload.maxPwr !== null) items.push({ k: t('social.messagesPage.summary.maxPower'), v: `${Math.round(payload.maxPwr)} W` });

  return (
    <div className="social-fit-summary">
      <div className="social-fit-summary-title">{t('social.messagesPage.summary.title')}</div>
      <div className="social-fit-grid">
        {items.map((it) => (
          <div key={it.k} className="social-fit-item">
            <div className="social-fit-k">{it.k}</div>
            <div className="social-fit-v">{it.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SocialMessagesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const locationState = location.state || {};
  const initialMatchId = locationState.initialMatchId || null;
  const fromMatch = !!locationState.fromMatch;
  const matchContextFromNav = locationState.matchContext || null;

  const [conversations, setConversations] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newText, setNewText] = useState('');
  const [error, setError] = useState('');
  const [loadingConvos, setLoadingConvos] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [matchHero, setMatchHero] = useState(matchContextFromNav);

  const messagesEndRef = useRef(null);

  const userId = useMemo(
    () => localStorage.getItem('userId') || localStorage.getItem('userID') || '',
    []
  );

  const myName = useMemo(
    () => localStorage.getItem('userName') || localStorage.getItem('username') || '',
    []
  );

  const myRole = useMemo(() => localStorage.getItem('userRole') || 'athlete', []);
  const defaultMyPhoto = useMemo(
    () =>
      myRole === 'trainer'
        ? `${HOST}/uploads/trainers/default.jpg`
        : `${HOST}/uploads/athletes/default.jpg`,
    [myRole]
  );

  // ==========================
  //   Modal ver ruta
  // ==========================
  const [routeViewerOpen, setRouteViewerOpen] = useState(false);
  const [routeViewerFileId, setRouteViewerFileId] = useState('');
  const [routeViewerTab, setRouteViewerTab] = useState('map');

  const openRouteViewer = (fileId, tab = 'map') => {
    if (!fileId) return;
    setRouteViewerFileId(String(fileId));
    setRouteViewerTab(tab === 'summary' ? 'summary' : 'map');
    setRouteViewerOpen(true);
  };

  const closeRouteViewer = () => {
    setRouteViewerOpen(false);
    setRouteViewerFileId('');
    setRouteViewerTab('map');
  };

  // ==========================
  //   Modal compartir ruta
  // ==========================
  const [shareRouteOpen, setShareRouteOpen] = useState(false);
  const [shareFileId, setShareFileId] = useState('');
  const [shareCaption, setShareCaption] = useState(t('social.messagesPage.defaultRouteCaption'));
  const [shareUploading, setShareUploading] = useState(false);
  const [shareErr, setShareErr] = useState('');
  const [sharePreviewTab, setSharePreviewTab] = useState('map');

  const openShareRoute = () => {
    setShareErr('');
    setShareFileId('');
    setShareCaption(t('social.messagesPage.defaultRouteCaption'));
    setShareUploading(false);
    setSharePreviewTab('map');
    setShareRouteOpen(true);
  };

  const closeShareRoute = () => {
    setShareRouteOpen(false);
    setShareErr('');
    setShareFileId('');
    setShareCaption(t('social.messagesPage.defaultRouteCaption'));
    setShareUploading(false);
    setSharePreviewTab('map');
  };

  const handleUploadFitForShare = async (file) => {
    if (!file) return;
    if (!userId) {
      setShareErr(t('social.messagesPage.errors.userIdNotFound'));
      return;
    }

    setShareErr('');
    setShareUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', userId);

      const resp = await fetch(`${API_URL}/api/fit/upload`, {
        method: 'POST',
        body: formData,
      });

      const result = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(
          result.message ||
            t('social.messagesPage.errors.uploadFit')
        );
      }

      if (!result.fileId) {
        throw new Error(t('social.messagesPage.errors.serverDidNotReturnFileId'));
      }

      setShareFileId(String(result.fileId));
    } catch (e) {
      setShareErr(String(e.message || e));
    } finally {
      setShareUploading(false);
    }
  };

  // ==========================
  //   Scroll automático al final
  // ==========================
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // ==========================
  //   Enviar mensaje
  // ==========================
  const postMessage = useCallback(
    async (text) => {
      if (!selectedMatch) return null;

      if (selectedMatch?.otherUser?.isBlocked) {
        setError(t('social.messagesPage.errors.blockedUserCannotMessage'));
        return null;
      }

      const mid = String(selectedMatch?.matchId || '').trim();
      if (!isObjectId(mid)) {
        setError(t('social.messagesPage.errors.invalidMatchId'));
        return null;
      }

      const res = await fetch(`${API_URL}/api/community/messages`, {
        method: 'POST',
        headers: buildHeaders(userId, { json: true }),
        body: JSON.stringify({
          matchId: mid,
          text: String(text || '').trim(),
        }),
      });

      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || t('social.messagesPage.errors.cannotSendYet'));
        return null;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      return data?.message || null;
    },
    [selectedMatch, userId, t]
  );

  // ==========================
  //   Cargar mensajes de un match
  // ==========================
  const loadMessages = useCallback(
    async (match, { markAsRead = true, preserveHero = false } = {}) => {
      if (!match) return;

      const mid = String(match?.matchId || '').trim();
      if (!isObjectId(mid)) {
        setSelectedMatch(match);
        setMessages([]);
        setError(t('social.messagesPage.errors.invalidConversationMatchId'));
        return;
      }

      try {
        setLoadingMessages(true);
        setError('');
        setSelectedMatch(match);

        if (!preserveHero) setMatchHero(null);

        const res = await fetch(`${API_URL}/api/community/messages/${mid}`, {
          headers: buildHeaders(userId),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json().catch(() => ({}));

        // backend: { ok:true, matchId, messages:[...] } (o array legacy)
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.messages)
            ? data.messages
            : [];

        setMessages(list);

        if (markAsRead) {
          try {
            await fetch(`${API_URL}/api/community/matches/${mid}/read`, {
              method: 'POST',
              headers: buildHeaders(userId, { json: true }),
              body: JSON.stringify({}),
            });
          } catch (err) {
            console.warn('[SocialMessages] error marcando como leído', err);
          }

          setConversations((prev) => prev.map((c) => (c.matchId === mid ? { ...c, unreadCount: 0 } : c)));
        }
      } catch (err) {
        console.error('[SocialMessages] error cargando mensajes', err);
        setError(t('social.messagesPage.errors.loadMessages'));
        setMessages([]);
      } finally {
        setLoadingMessages(false);
      }
    },
    [userId, t]
  );

  // ==========================
  //   Cargar matches activos
  // ==========================
  useEffect(() => {
    const loadConversations = async () => {
      if (!userId) {
        setError(t('social.messagesPage.errors.currentUserNotFound'));
        return;
      }

      try {
        setLoadingConvos(true);
        setError('');

        const res = await fetch(`${API_URL}/api/community/matches?status=active`, {
          headers: buildHeaders(userId),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const raw = await res.json();
        const matches = Array.isArray(raw) ? raw : raw?.matches || raw?.items || [];

        const mapped = (Array.isArray(matches) ? matches : [])
          .map((m) => {
            const ou = m.otherUser || {};
            const role = ou.role || ou.userRole || 'athlete';
            const rawPhoto = ou.photoUrl || ou.avatarUrl || ou.profilePhoto || '';
            const photoUrl = resolvePhotoUrl(rawPhoto, role);
            const isBlocked = !!(m.isBlocked || ou.isBlocked);
            const matchId = pickMatchId(m);

            return {
              matchId,
              otherUser: {
                id: ou.id || ou.userId || ou._id || '',
                name: ou.name || t('social.partnerTzone'),
                role,
                photoUrl,
                isBlocked,
              },
              lastMessageText: m.lastMessageText || '',
              lastMessageAt: m.lastMessageAt || null,
              unreadCount: m.unreadCount || 0,
            };
          })
          .filter((c) => isObjectId(String(c.matchId || '').trim()));

        setConversations(mapped);

        if (initialMatchId) {
          const found = mapped.find((c) => String(c.matchId) === String(initialMatchId));

          if (found) {
            if (fromMatch && !matchHero) {
              setMatchHero(
                matchContextFromNav || {
                  me: {
                    id: userId,
                    name: myName || t('social.messagesPage.you'),
                    photoUrl: defaultMyPhoto,
                  },
                  other: {
                    id: found.otherUser.id,
                    name: found.otherUser.name,
                    photoUrl: found.otherUser.photoUrl || '',
                  },
                }
              );
            }

            loadMessages(found, { markAsRead: true, preserveHero: true });
          }
        }
      } catch (err) {
        console.error('[SocialMessages] error cargando matches', err);
        setError(t('social.messagesPage.errors.loadConversations'));
      } finally {
        setLoadingConvos(false);
      }
    };

    loadConversations();
  }, [userId, initialMatchId, fromMatch, matchContextFromNav, matchHero, myName, defaultMyPhoto, loadMessages, t]);

  // ==========================
  //   Enviar mensaje (texto)
  // ==========================
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newText.trim() || !selectedMatch) return;

    try {
      setError('');
      const sent = await postMessage(newText.trim());
      if (!sent) return;

      setMessages((prev) => [...prev, sent]);
      setNewText('');
      setMatchHero(null);

      const mid = String(selectedMatch?.matchId || '').trim();
      setConversations((prev) =>
        prev.map((c) =>
          c.matchId === mid
            ? {
                ...c,
                lastMessageText: sent.text,
                unreadCount: 0,
              }
            : c
        )
      );
    } catch (err) {
      console.error('[SocialMessages] error enviando mensaje', err);
      setError(t('social.messagesPage.errors.sendMessage'));
    }
  };

  // ==========================
  //   Compartir ruta
  // ==========================
  const handleSendRoute = async () => {
    if (!selectedMatch) return;
    if (!shareFileId.trim()) {
      setShareErr(t('social.messagesPage.errors.validFileIdRequired'));
      return;
    }

    const cleanId = shareFileId.trim();
    const caption = (shareCaption || t('social.messagesPage.defaultRouteCaption')).trim();
    const payloadText = `${caption} [tzone-fit-route:${cleanId}]`;

    try {
      setShareErr('');
      setError('');

      const sent = await postMessage(payloadText);
      if (!sent) return;

      setMessages((prev) => [...prev, sent]);

      const mid = String(selectedMatch?.matchId || '').trim();
      setConversations((prev) =>
        prev.map((c) => (c.matchId === mid ? { ...c, lastMessageText: sent.text, unreadCount: 0 } : c))
      );

      closeShareRoute();
      setMatchHero(null);
    } catch (e) {
      console.error('[SocialMessages] error enviando ruta', e);
      setShareErr(t('social.messagesPage.errors.shareRoute'));
    }
  };

  // ==========================
  //   Bloquear / desbloquear usuario
  // ==========================
  const handleToggleBlockUser = async () => {
    if (!selectedMatch || !selectedMatch.otherUser) return;
    const targetUserId = selectedMatch.otherUser.id;
    if (!targetUserId) return;

    const currentlyBlocked = !!selectedMatch.otherUser.isBlocked;

    const ok = window.confirm(
      currentlyBlocked
        ? t('social.messagesPage.confirmUnblockUser')
        : t('social.messagesPage.confirmBlockUser')
    );
    if (!ok) return;

    try {
      const endpoint = currentlyBlocked ? `${API_URL}/api/community/unblock` : `${API_URL}/api/community/block`;

      await fetch(endpoint, {
        method: 'POST',
        headers: buildHeaders(userId, { json: true }),
        body: JSON.stringify({ targetUserId }),
      });

      const mid = String(selectedMatch?.matchId || '').trim();

      setConversations((prev) =>
        prev.map((c) =>
          c.matchId === mid
            ? {
                ...c,
                otherUser: {
                  ...c.otherUser,
                  isBlocked: !currentlyBlocked,
                },
              }
            : c
        )
      );

      setSelectedMatch((prev) =>
        prev
          ? {
              ...prev,
              otherUser: {
                ...prev.otherUser,
                isBlocked: !currentlyBlocked,
              },
            }
          : prev
      );

      setError('');
    } catch (err) {
      console.error('[SocialMessages] error bloqueando/desbloqueando', err);
      setError(
        currentlyBlocked
          ? t('social.messagesPage.errors.unblockUser')
          : t('social.messagesPage.errors.blockUser')
      );
    }
  };

  // ==========================
  //   Denunciar usuario
  // ==========================
  const handleReportUser = async () => {
    if (!selectedMatch || !selectedMatch.otherUser) return;
    const targetUserId = selectedMatch.otherUser.id;
    if (!targetUserId) return;

    const ok = window.confirm(t('social.messagesPage.confirmReportUser'));
    if (!ok) return;

    try {
      await fetch(`${API_URL}/api/community/report`, {
        method: 'POST',
        headers: buildHeaders(userId, { json: true }),
        body: JSON.stringify({
          targetUserId,
          reason: 'inappropriate',
          description: '',
        }),
      });

      alert(t('social.messagesPage.reportSuccess'));
    } catch (err) {
      console.error('[SocialMessages] error reportando usuario', err);
      setError(t('social.messagesPage.errors.reportUser'));
    }
  };

  const heroMePhoto = (matchHero && matchHero.me && matchHero.me.photoUrl) || defaultMyPhoto;
  const heroOtherPhoto = (matchHero && matchHero.other && matchHero.other.photoUrl) || '';
  const heroOtherName =
    (matchHero && matchHero.other && matchHero.other.name) ||
    (selectedMatch && selectedMatch.otherUser.name) ||
    t('social.messagesPage.athleteTzone');

  const selectedOtherName = selectedMatch?.otherUser?.name || t('social.partnerTzone');
  const selectedOtherPhoto = selectedMatch?.otherUser?.photoUrl || '';
  const isBlocked = !!selectedMatch?.otherUser?.isBlocked;

  const copyToClipboard = async (txt) => {
    try {
      await navigator.clipboard.writeText(String(txt || ''));
    } catch (_) {}
  };

  return (
    <div className="social-messages-root">
      <header className="social-messages-header">
        <button type="button" className="social-messages-back" onClick={() => navigate('/social')}>
          ⬅ {t('social.searchPartner')}
        </button>
        <h2>{t('social.messages')}</h2>
      </header>

      <div className="social-messages-layout">
        {/* Columna izquierda: conversaciones */}
        <aside className="social-messages-list">
          <h4>{t('social.messagesPage.conversations')}</h4>
          {loadingConvos && <p>{t('social.loading')}</p>}
          {!loadingConvos && !conversations.length && (
            <p>{t('social.messagesPage.noConversations')}</p>
          )}

          <ul>
            {conversations.map((c) => {
              const otherName = c?.otherUser?.name || t('social.messagesPage.user');
              const otherPhoto = c?.otherUser?.photo || c?.otherUser?.photoUrl || c?.otherUser?.avatar || '';
              const otherBlocked = !!c?.otherUser?.isBlocked;

              return (
                <li
                  key={c.matchId}
                  className={
                    'social-messages-item' +
                    (selectedMatch && selectedMatch.matchId === c.matchId ? ' social-messages-item--active' : '')
                  }
                  onClick={() => loadMessages(c)}
                >
                  <div className="social-messages-item-row">
                    {/* Avatar */}
                    <div className="social-messages-item-avatar" aria-label={otherName}>
                      {/* fallback iniciales */}
                      <span className="social-messages-item-avatar-fallback">
                        {initialsFromName(otherName)}
                      </span>

                      {/* si hay foto, se superpone al fallback */}
                      {otherPhoto ? (
                        <img
                          src={otherPhoto}
                          alt={otherName}
                          onError={(e) => {
                            // si falla la imagen, mostramos solo fallback
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : null}
                    </div>

                    {/* Texto */}
                    <div className="social-messages-item-text">
                      <div className="social-messages-item-name">
                        {otherName}
                        {otherBlocked && <span className="social-messages-item-badge-blocked">{t('social.contacts.blocked')}</span>}
                      </div>

                      {c.lastMessageText && (
                        <div className="social-messages-item-last">{prettyLastMessage(c.lastMessageText, t)}</div>
                      )}
                    </div>
                  </div>

                  {c.unreadCount > 0 && <span className="social-messages-item-unread">{c.unreadCount}</span>}
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Columna derecha: chat */}
        <section className="social-messages-chat">
          {!selectedMatch ? (
            <div className="social-messages-empty">
              <p>{t('social.messagesPage.selectConversation')}</p>
            </div>
          ) : (
            <>
              {/* 🎉 Hero tipo Bumble */}
              {matchHero && (
                <div className="social-match-hero">
                  <p className="social-match-title">{t('social.messagesPage.matchNowCanChat')}</p>

                  <div className="social-match-avatars-row">
                    {heroOtherPhoto && (
                      <div className="social-match-avatar">
                        <img src={heroOtherPhoto} alt={heroOtherName} />
                        <span className="social-match-avatar-name">{heroOtherName}</span>
                      </div>
                    )}

                    <div className="social-match-avatar social-match-avatar--me">
                      <img src={heroMePhoto} alt={myName || t('social.messagesPage.you')} />
                      <span className="social-match-avatar-name">{myName || t('social.messagesPage.you')}</span>
                    </div>
                  </div>

                  <button type="button" className="social-match-cta" onClick={() => setMatchHero(null)}>
                    {t('social.messagesPage.startChat')} ✕
                  </button>
                </div>
              )}

              {/* Header del chat con avatar + acciones */}
              <div className="social-messages-chat-header">
                <div className="social-chat-header-user">
                  <div className="social-chat-header-avatar">
                    {selectedOtherPhoto ? (
                      <img src={selectedOtherPhoto} alt={selectedOtherName} />
                    ) : (
                      <span>{selectedOtherName.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="social-chat-header-meta">
                    <span className="social-chat-header-name">{selectedOtherName}</span>
                    <span className={'social-chat-header-sub' + (isBlocked ? ' social-chat-header-sub--blocked' : '')}>
                      {isBlocked ? t('social.messagesPage.blockedUserNoMessages') : t('social.messagesPage.trainingPartner')}
                    </span>
                  </div>
                </div>

                <div className="social-chat-header-actions">
                  <button
                    type="button"
                    className="social-chat-header-btn social-chat-header-btn--route"
                    onClick={openShareRoute}
                    title={t('social.shareActivityFit')}
                    disabled={isBlocked}
                  >
                    🗺️
                  </button>

                  <button
                    type="button"
                    className={
                      'social-chat-header-btn ' +
                      (isBlocked ? 'social-chat-header-btn--unblock' : 'social-chat-header-btn--block')
                    }
                    onClick={handleToggleBlockUser}
                    title={isBlocked ? t('social.contacts.unblock') : t('social.contacts.block')}
                  >
                    {isBlocked ? '✅' : '🚫'}
                  </button>

                  <button
                    type="button"
                    className="social-chat-header-btn social-chat-header-btn--report"
                    onClick={handleReportUser}
                    title={t('social.report')}
                  >
                    ⚠️
                  </button>
                </div>
              </div>

              <div className="social-messages-chat-body">
                {loadingMessages && <p>{t('social.messagesPage.loadingMessages')}</p>}

                {!loadingMessages &&
                  messages.map((msg) => {
                    const mine = String(msg.from) === String(userId);
                    const parsed = parseRouteMessage(msg.text);

                    return (
                      <div
                        key={msg._id}
                        className={
                          'social-messages-bubble ' + (mine ? 'social-messages-bubble--me' : 'social-messages-bubble--other')
                        }
                      >
                        {!parsed.isRoute ? (
                          <span>{msg.text}</span>
                        ) : (
                          <div className="social-route-card--rich">
                            <div className="social-route-head">
                              <div className="social-route-title">{parsed.caption}</div>
                              <div className="social-route-sub">
                                ID: <span className="social-route-id">{parsed.fileId}</span>
                              </div>
                            </div>

                            <div className="social-route-actions">
                              <button type="button" className="social-route-btn" onClick={() => openRouteViewer(parsed.fileId, 'map')}>
                                {t('social.messagesPage.viewMap')}
                              </button>

                              <button
                                type="button"
                                className="social-route-btn"
                                onClick={() => openRouteViewer(parsed.fileId, 'summary')}
                              >
                                {t('social.messagesPage.viewSummary')}
                              </button>

                              <button
                                type="button"
                                className="social-route-btn"
                                onClick={async () => {
                                  try {
                                    await downloadGpx(parsed.fileId, userId, pickMatchId(selectedMatch));
                                  } catch (e) {
                                    setError(e?.message || t('social.messagesPage.errors.downloadGpx'));
                                  }
                                }}
                              >
                                {t('social.messagesPage.downloadGpx')}
                              </button>

                              <button
                                type="button"
                                className="social-route-btn social-route-btn--ghost"
                                onClick={() => copyToClipboard(parsed.fileId)}
                                title={t('social.messagesPage.copyFileId')}
                              >
                                {t('social.messagesPage.copy')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                <div ref={messagesEndRef} />
              </div>

              <form className="social-messages-chat-input" onSubmit={handleSend}>
                <input
                  type="text"
                  placeholder={
                    isBlocked
                      ? t('social.messagesPage.unblockToWrite')
                      : t('social.messagesPage.writeMessage')
                  }
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  disabled={isBlocked}
                />
                <button type="submit" disabled={isBlocked}>
                  {t('common.send')}
                </button>
              </form>

              {error && <div className="social-messages-error">{error}</div>}
            </>
          )}
        </section>
      </div>

      {/* ==========================
          MODAL: VER RUTA
         ========================== */}
      {routeViewerOpen && (
        <div className="social-modal-backdrop" onClick={closeRouteViewer}>
          <div className="social-modal-card social-modal-card--xl" onClick={(e) => e.stopPropagation()}>
            <div className="social-modal-header">
              <div className="social-modal-title">{t('social.messagesPage.activityDetail')}</div>

              <div className="social-modal-tabs">
                <button
                  type="button"
                  className={'social-modal-tab' + (routeViewerTab === 'map' ? ' is-active' : '')}
                  onClick={() => setRouteViewerTab('map')}
                >
                  {t('social.messagesPage.map')}
                </button>
                <button
                  type="button"
                  className={'social-modal-tab' + (routeViewerTab === 'summary' ? ' is-active' : '')}
                  onClick={() => setRouteViewerTab('summary')}
                >
                  {t('social.messagesPage.summaryTab')}
                </button>
              </div>

              <button type="button" className="social-modal-close" onClick={closeRouteViewer}>
                ✕
              </button>
            </div>

            <div className="social-modal-body">
              {!routeViewerFileId ? (
                <div>{t('social.messagesPage.noFileIdToShow')}</div>
              ) : routeViewerTab === 'map' ? (
                <GeoLocationComponent fileId={routeViewerFileId} />
              ) : (
                <FitSummaryCard fileId={routeViewerFileId} userId={userId} matchId={pickMatchId(selectedMatch)} t={t} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==========================
          MODAL: COMPARTIR RUTA
         ========================== */}
      {shareRouteOpen && (
        <div className="social-modal-backdrop" onClick={closeShareRoute}>
          <div className="social-modal-card social-modal-card--xl" onClick={(e) => e.stopPropagation()}>
            <div className="social-modal-header">
              <div className="social-modal-title">{t('social.shareActivityFit')}</div>
              <button type="button" className="social-modal-close" onClick={closeShareRoute}>
                ✕
              </button>
            </div>

            <div className="social-modal-body">
              <div className="social-share-grid">
                <div className="social-share-block">
                  <label className="social-share-label">{t('social.messagesPage.title')}</label>
                  <input
                    className="social-share-input"
                    value={shareCaption}
                    onChange={(e) => setShareCaption(e.target.value)}
                    placeholder={t('social.messagesPage.routeTitlePlaceholder')}
                  />
                </div>

                <div className="social-share-block">
                  <label className="social-share-label">{t('social.messagesPage.fileIdFit')}</label>
                  <input
                    className="social-share-input"
                    value={shareFileId}
                    onChange={(e) => setShareFileId(e.target.value)}
                    placeholder={t('social.messagesPage.fileIdPlaceholder')}
                  />
                  <div className="social-share-hint">{t('social.messagesPage.fileIdHint')}</div>
                </div>

                <div className="social-share-divider" />

                <div className="social-share-block">
                  <label className="social-share-label">{t('social.messagesPage.optionalUploadFitNow')}</label>

                  <input
                    type="file"
                    accept=".fit"
                    onChange={(e) => handleUploadFitForShare(e.target.files?.[0])}
                    disabled={shareUploading}
                  />

                  {shareUploading && <div className="social-share-hint">{t('social.messagesPage.uploading')}</div>}

                  {shareErr && <div className="social-share-error">{shareErr}</div>}
                </div>

                {shareFileId?.trim() && (
                  <div className="social-share-preview">
                    <div className="social-share-preview-head">
                      <div className="social-share-preview-title">{t('social.messagesPage.preview')}</div>

                      <div className="social-modal-tabs social-modal-tabs--compact">
                        <button
                          type="button"
                          className={'social-modal-tab' + (sharePreviewTab === 'map' ? ' is-active' : '')}
                          onClick={() => setSharePreviewTab('map')}
                        >
                          {t('social.messagesPage.map')}
                        </button>
                        <button
                          type="button"
                          className={'social-modal-tab' + (sharePreviewTab === 'summary' ? ' is-active' : '')}
                          onClick={() => setSharePreviewTab('summary')}
                        >
                          {t('social.messagesPage.summaryTab')}
                        </button>
                      </div>
                    </div>

                    {sharePreviewTab === 'map' ? (
                      <GeoLocationComponent fileId={shareFileId.trim()} height={320} showElevation={false} />
                    ) : (
                      <FitSummaryCard fileId={shareFileId.trim()} userId={userId} matchId={pickMatchId(selectedMatch)} t={t} />
                    )}
                  </div>
                )}

                <div className="social-share-actions">
                  <button
                    type="button"
                    className="social-route-btn"
                    onClick={handleSendRoute}
                    disabled={isBlocked || !shareFileId.trim()}
                  >
                    {t('social.shareInChat')}
                  </button>

                  <button type="button" className="social-route-btn social-route-btn--ghost" onClick={closeShareRoute}>
                    {t('common.cancel')}
                  </button>
                </div>

                {isBlocked && (
                  <div className="social-share-error">{t('social.messagesPage.unblockToShareRoutes')}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMessagesPage;
